import { Avatar } from '@mui/material';
import { MobileDateTimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { Button, Label, TextInput } from 'flowbite-react';
import React, { useCallback, useEffect, useState } from 'react';
import { HiLocationMarker } from 'react-icons/hi';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { stringAvatar } from 'shared-utils';
import PreferenceWrapper from 'src/components/dashboard/dashboardTopSection/scheduleInterviewDrawer/leftSection/PreferenceWrapper';
import PreferenceTab from 'src/components/dashboard/dashboardTopSection/scheduleInterviewDrawer/PreferenceTab';
import Container from 'src/components/shared/Container';
import { useToast } from 'src/context/ToastProvider';
import { useUserContext } from 'src/context/UserProvider';
import { useGetCompanyByIdQuery } from 'src/hooks/company.hooks';
import { useGetAllTemplateConfig } from 'src/hooks/templateConfig.hooks';
import { generateCompanyLogoAvatarSrc } from 'src/utils/companyLogo';
import {
	AssessmentDuration,
	DevelopmentRoleType,
	TechStackType,
	UITemplateConfig,
	UserCareerPathType,
	UserExperienceLevelType,
	UserPreferenceType,
	WorkingMode,
} from 'types';

const CreateHiringAssessment = () => {
	const { data: templateConfigs } = useGetAllTemplateConfig();
	const [allTemplateConfigs, setAllTemplateConfigs] = React.useState<UITemplateConfig[]>([]);
	useEffect(() => {
		if (templateConfigs) {
			setAllTemplateConfigs(templateConfigs.data);
		}
	}, [templateConfigs, templateConfigs?.data]);

	const { user } = useUserContext();
	const {
		data: companyData,
		isLoading: isCompanyLoading,
		isError,
	} = useGetCompanyByIdQuery(user?.registeredCompany?._id ?? '');
	const [userPreference, setUserPreference] = React.useState<UserPreferenceType>();
	const [salaryRange, setSalaryRange] = useState<{ min?: number; max?: number } | null>(null);
	const [workingMode, setWorkingMode] = useState<WorkingMode | null>(null);
	const labelStyle = `dark:text-white text-[#111928]`;
	const navigate = useNavigate();
	const toast = useToast();
	const allExperienceLevels = Object.values(UserExperienceLevelType).filter(
		experienceLevel => experienceLevel !== UserExperienceLevelType.FINAL_YEAR_STUDENT,
	);

	const [experienceLevel, setExperienceLevel] = useState<UserExperienceLevelType>(
		UserExperienceLevelType.FINAL_YEAR_STUDENT,
	);
	const selectJobType = Object.values(WorkingMode);
	const [careerPaths, setCareerPaths] = React.useState<string[]>([]);
	const [developmentRoles, setDevelopmentRoles] = React.useState<string[]>([]);
	const [techStacks, setTechStacks] = React.useState<string[]>([]);
	const [avatarImg, setAvatarImg] = useState('');
	const { benchmarkingFormData, setBenchmarkingFormData } = useUserContext();
	// set development roles
	useEffect(() => {
		if (userPreference?.careerPath) {
			if (userPreference.careerPath) {
				const developmentRoles = allTemplateConfigs
					.filter(
						config => config.experienceLevel === experienceLevel && config.careerPath === userPreference.careerPath,
					)
					.map(config => config.developmentRole);
				setDevelopmentRoles(Array.from(new Set(developmentRoles)));
			}
		}
	}, [allTemplateConfigs, experienceLevel, userPreference, userPreference?.careerPath]);

	// set tech stacks
	useEffect(() => {
		if (userPreference?.developmentRole && userPreference.careerPath) {
			const techStacks = allTemplateConfigs
				.filter(
					config =>
						config.experienceLevel === experienceLevel &&
						config.careerPath === userPreference.careerPath &&
						config.developmentRole === userPreference.developmentRole,
				)
				.map(config => config.techStack);
			if (techStacks) {
				setTechStacks(Array.from(new Set(techStacks)));
			}
		}
	}, [
		userPreference,
		userPreference?.developmentRole,
		userPreference?.careerPath,
		allTemplateConfigs,
		experienceLevel,
	]);

	const generateAvatar = useCallback(async () => {
		if (user?.registeredCompany?.logo) {
			const res = await generateCompanyLogoAvatarSrc(
				user?.registeredCompany?.logo ?? '',
				user?.registeredCompany?.url ?? '',
			);

			setAvatarImg(res);
		} else if (user?.registeredCompany) {
			const res = await generateCompanyLogoAvatarSrc(
				user?.registeredCompany?.logo ?? '',
				user?.registeredCompany?.url ?? '',
			);

			setAvatarImg(res);
		}
	}, [user?.registeredCompany?.logo]);

	useEffect(() => {
		generateAvatar();
	}, [user?.registeredCompany?.logo, user?.registeredCompany?.url]);

	useEffect(() => {
		if (benchmarkingFormData?.userPreferenceSnapshot) {
			const experienceLevel = benchmarkingFormData.userPreferenceSnapshot.experienceLevel;
			setExperienceLevel(experienceLevel || UserExperienceLevelType.FRESHER);
			const careerPaths = allTemplateConfigs
				.filter(config => config.experienceLevel === experienceLevel)
				.map(config => config.careerPath);
			setCareerPaths(Array.from(new Set(careerPaths)));

			setUserPreference({
				experienceLevel: benchmarkingFormData.userPreferenceSnapshot.experienceLevel,
				careerPath: benchmarkingFormData.userPreferenceSnapshot.careerPath,
				developmentRole: benchmarkingFormData.userPreferenceSnapshot.developmentRole,
				techStack: benchmarkingFormData.userPreferenceSnapshot.techStack,
			});

			if (benchmarkingFormData.userPreferenceSnapshot.careerPath) {
				const careerPath = benchmarkingFormData.userPreferenceSnapshot.careerPath;
				if (careerPath) {
					const developmentRoles = allTemplateConfigs
						.filter(config => config.experienceLevel === experienceLevel && config.careerPath === careerPath)
						.map(config => config.developmentRole);
					setDevelopmentRoles(Array.from(new Set(developmentRoles)));
				}
			}

			if (
				benchmarkingFormData.userPreferenceSnapshot.developmentRole &&
				benchmarkingFormData.userPreferenceSnapshot.careerPath
			) {
				const careerPath = benchmarkingFormData.userPreferenceSnapshot.careerPath;
				if (careerPath) {
					const developmentRoles = allTemplateConfigs
						.filter(config => config.experienceLevel === experienceLevel && config.careerPath === careerPath)
						.map(config => config.developmentRole);
					const role = benchmarkingFormData.userPreferenceSnapshot.developmentRole;
					const techStacks = allTemplateConfigs
						.filter(
							config =>
								config.experienceLevel === experienceLevel &&
								config.careerPath === careerPath &&
								config.developmentRole === role,
						)
						.map(config => config.techStack);
					if (role && techStacks) {
						setTechStacks(Array.from(new Set(techStacks)));
					}
				}
			}
		}
		if (benchmarkingFormData?.salaryRange) {
			setSalaryRange(benchmarkingFormData.salaryRange);
		}
		if (benchmarkingFormData?.assessmentDeadline) {
			setUserPreference(prevState => ({
				...prevState,
				newAssignmentDeadline: benchmarkingFormData.assessmentDeadline,
			}));
		}
		if (benchmarkingFormData?.workingMode) {
			setWorkingMode(benchmarkingFormData.workingMode);
		}
	}, [benchmarkingFormData]);

	return (
		<Container className="">
			<div className="flex items-center justify-center max-w-[580px] mx-auto py-5">
				<div className="flex flex-col bg-[#FFF] rounded-[12px] pt-[20px] relative">
					<div className="flex flex-col items-start gap-[16px] px-4 rounded-[12px]">
						<div className="flex justify-between items-center gap-2 w-full">
							<div>
								<div className="flex items-center gap-4 rounded-lg w-full">
									<Avatar
										alt="companyLogo"
										src={avatarImg}
										sx={{
											width: '51.2px',
											height: '51.2px',
											minWidth: '51.2px',
											minHeight: '51.2px',
											color: 'white',
											border: '1px solid #E5E5E5',
										}}
									>
										{stringAvatar(
											user?.registeredCompany?.name.trim().toUpperCase() || user?.registeredCompany?.name || '',
										)}
									</Avatar>
									<div className="flex pr-2 flex-col items-start gap-1 w-full">
										<div className="text-[20px] font-[700] leading-[120%] text-dark-0 font-inter text-[#28293D]">
											{user?.registeredCompany?.name}
										</div>
										<div className="flex items-center text-center gap-1">
											<HiLocationMarker className="w-[14px] h-[14px] object-contain text-[#00B7C4] dark:text-[#A9EFF2]" />

											<div className="text-[14px] font-normal leading-[20px] text-[#555770] font-inter">
												{companyData?.company?.headQuarter ? companyData.company.headQuarter.split(',')[0] : 'Remote'}
											</div>
										</div>
									</div>
								</div>
							</div>
							<div className="flex justify-center items-center text-center pr-[30px]">
								<Link
									to={`/edit-company/${user?.registeredCompany?._id}?navigateTo=goBack`}
									className="text-[#3E7BFA] text-base underline font-[400]"
								>
									Edit
								</Link>
							</div>
						</div>

						<div className="flex flex-col items-start gap-[8px]">
							<h6 className="text-[#28293D] font-inter text-base md:text-xl font-[700]">New Assessment</h6>
							<p className=" text-[#3E7BFA] font-inter text-xs md:text-base font-[400] ">
								Roll out an assessment to evaluate your current pipeline and shortlist candidates.
							</p>
						</div>
					</div>
					<div className="my-[16px] px-4">
						<div className="flex flex-col justify-end items-start gap-[32px] self-stretch rounded-[12px] pb-4">
							{/* Experience Level */}
							<PreferenceWrapper>
								<div className="flex items-center mb-4">
									<span className="font-inter text-sm not-italic font-[500] leading-[20px]">
										Select years of experience
									</span>
									<span className="text-red-500 font-inter text-sm not-italic font-medium leading-5 ml-1">*</span>
								</div>
								<div className="font-inter text-sm not-italic font-medium leading-5 flex flex-wrap gap-2">
									{allExperienceLevels.map(experienceLevel => (
										<PreferenceTab
											key={experienceLevel}
											text={experienceLevel}
											isSelected={experienceLevel === userPreference?.experienceLevel}
											onSelect={() => {
												setDevelopmentRoles([]);
												setTechStacks([]);
												setUserPreference(prevState => ({
													...prevState,
													experienceLevel: experienceLevel,
												}));

												setExperienceLevel(experienceLevel);
												const careerPaths = allTemplateConfigs
													.filter(config => config.experienceLevel === experienceLevel)
													.map(config => config.careerPath);
												setCareerPaths(Array.from(new Set(careerPaths)));
											}}
											// isDisabled={experienceLevel === UserExperienceLevelType.TwoPlusYears}
											// isComingSoon={experienceLevel === UserExperienceLevelType.TwoPlusYears}
										/>
									))}
								</div>
							</PreferenceWrapper>
							{/* Career Path */}

							{userPreference?.experienceLevel && (
								<PreferenceWrapper>
									<div className="flex items-center mb-4">
										<span className="font-inter text-sm not-italic font-medium leading-5">
											Select a preferred domain
										</span>
										<span className="text-red-500 font-inter text-sm not-italic font-medium leading-5 ml-1">*</span>
									</div>
									<div className="font-inter text-sm not-italic font-medium leading-5 flex flex-wrap gap-2">
										{careerPaths.map(careerPath => (
											<PreferenceTab
												key={careerPath}
												text={careerPath}
												isSelected={careerPath === userPreference?.careerPath}
												onSelect={() => {
													setDevelopmentRoles([]);
													setTechStacks([]);
													setUserPreference(prevState => ({
														...prevState,
														experienceLevel: userPreference.experienceLevel,
														careerPath: careerPath as UserCareerPathType,
													}));
												}}
											/>
										))}
									</div>
								</PreferenceWrapper>
							)}
							{/* Development Role*/}
							{userPreference?.careerPath &&
								developmentRoles.length > 0 &&
								!developmentRoles.includes(DevelopmentRoleType.OTHER) && (
									<PreferenceWrapper>
										<div className="flex items-center mb-4">
											<span className="font-inter text-sm not-italic font-medium leading-5">
												Select a preferred role
											</span>
											<span className="text-red-500 font-inter text-sm not-italic font-medium leading-5 ml-1">*</span>
										</div>
										<div className="font-inter text-sm not-italic font-medium leading-5 flex flex-wrap gap-2">
											{developmentRoles.map(developmentRole => (
												<PreferenceTab
													key={developmentRole}
													text={developmentRole}
													isSelected={developmentRole === userPreference?.developmentRole}
													onSelect={() => {
														setTechStacks([]);
														setUserPreference(prevState => ({
															...prevState,
															experienceLevel: userPreference.experienceLevel,
															careerPath: userPreference.careerPath,
															developmentRole: developmentRole as DevelopmentRoleType,
														}));
													}}
												/>
											))}
										</div>
									</PreferenceWrapper>
								)}
							{userPreference?.developmentRole &&
								techStacks.length > 0 &&
								!techStacks.includes(TechStackType.OTHER) && (
									<PreferenceWrapper>
										<div className="flex items-center pb-[16px]">
											<span className="font-inter text-sm not-italic font-medium leading-5">
												Select a preferred tech stack
											</span>
											<span className="text-red-500 font-inter text-sm not-italic font-medium leading-5 ml-1">*</span>
										</div>
										<div className="font-inter text-sm not-italic font-medium leading-5 flex flex-wrap gap-2">
											{techStacks.map(techStack => (
												<PreferenceTab
													key={techStack}
													text={techStack}
													isSelected={techStack === userPreference?.techStack}
													onSelect={() => {
														setUserPreference(prevState => ({
															...prevState,
															experienceLevel: userPreference.experienceLevel,
															careerPath: userPreference.careerPath,
															developmentRole: userPreference.developmentRole,
															techStack: techStack as TechStackType,
														}));
													}}
												/>
											))}
										</div>
									</PreferenceWrapper>
								)}
							{/* Job Type */}
							<PreferenceWrapper>
								<div className="flex items-center mb-4">
									<span className="font-inter text-sm not-italic font-[500] leading-[20px]">Select job type</span>
									<span className="text-red-500 font-inter text-sm not-italic font-medium leading-5 ml-1">*</span>
								</div>
								<div className="font-inter text-sm not-italic font-medium leading-5 flex flex-wrap gap-2">
									{selectJobType.map(jobType => (
										<PreferenceTab
											key={jobType}
											text={jobType}
											isSelected={jobType === workingMode}
											onSelect={() => {
												setWorkingMode(jobType as WorkingMode);
											}}
										/>
									))}
								</div>
							</PreferenceWrapper>
							{/* Assessment Duration */}
							{/* <PreferenceWrapper>
								<div className="flex items-center mb-4">
									<span className="font-inter text-sm not-italic font-[500] leading-[20px]">
										Select assessment duration
									</span>
									<span className="text-red-500 font-inter text-sm not-italic font-medium leading-5 ml-1">*</span>
								</div>
								<div className="font-inter text-sm not-italic font-medium leading-5 flex flex-wrap gap-2">
									{assessmentDuration.map(duration => (
										<PreferenceTab
											key={duration}
											text={duration.toLowerCase().replace(/\s/g, '')}
											isSelected={duration === userPreference?.assessmentDuration}
											onSelect={() => {
												setUserPreference(prevState => ({
													...prevState,
													assessmentDuration: duration as AssessmentDuration,
												}));
											}}
										/>
									))}
								</div>
							</PreferenceWrapper> */}
							{/* Salary Range */}
							<div>
								<div className="mb-4 block">
									<Label htmlFor="title" value="Salary range (lakhs per annum)" className={`${labelStyle}`} />
									<span className="text-red-600 pl-1">*</span>
								</div>

								<div className="flex items-center gap-2">
									<TextInput
										min={0}
										max={80}
										step="any"
										onWheel={e => document.activeElement instanceof HTMLElement && document.activeElement.blur()}
										type="number"
										placeholder="0"
										name="salaryMin"
										value={salaryRange?.min ?? ''}
										onChange={e => {
											const selectedValue = +e.target.value;
											if (!isNaN(selectedValue)) {
												setSalaryRange({
													...salaryRange,
													min: selectedValue,
												});
											}
										}}
										className="[&_input:focus]:ring-transparent [&_input:focus]:border-[#1C64F2] [&_input]:text-center RemoveInputArrow max-w-[50px]"
									/>
									<div className="h-[1px] w-[14px] bg-[#C7C9D9]"></div>
									<TextInput
										min={0}
										max={80}
										step="any"
										onWheel={e => document.activeElement instanceof HTMLElement && document.activeElement.blur()}
										type="number"
										placeholder="2"
										name="salaryMax"
										value={salaryRange?.max ?? ''}
										onChange={e => {
											const selectedValue = +e.target.value;
											if (!isNaN(selectedValue)) {
												setSalaryRange({
													...salaryRange,
													max: selectedValue,
												});
											}
										}}
										className="[&_input:focus]:ring-transparent [&_input:focus]:border-[#1C64F2] [&_input]:text-center RemoveInputArrow max-w-[50px]"
									/>
									LPA
								</div>
							</div>
							<div className="w-full">
								<div className="mb-[8px]">
									<Label>{'Assessment deadline (should be minimum 12 hours)'}</Label>
								</div>

								<MobileDateTimePicker
									minDateTime={dayjs().add(12, 'hour')}
									format="DD MMM, YYYY, hh:mm A"
									minutesStep={1}
									value={
										benchmarkingFormData?.assessmentDeadline ? dayjs(benchmarkingFormData?.assessmentDeadline) : null
									}
									onChange={date => {
										const dateValue = date as { $d?: Date };
										setUserPreference(prevState => ({
											...prevState,
											newAssignmentDeadline: dateValue?.$d ?? new Date(),
										}));
									}}
									sx={{
										'&': { width: '100%' },
										'& .MuiInputBase-input': {
											padding: '10px',
											borderRadius: '8px',
											backgroundColor: 'white',
											border: '1px solid rgb(209 213 219 / 1)',
											fontSize: '14px',
										},
										'& .MuiInputBase-root': {
											outline: 'none',
											padding: '0px',
											'&:hover': { outline: 'none' },
										},
										'& fieldset': { display: 'none' },
									}}
								/>
							</div>
						</div>
					</div>
					<div
						className="bg-white dark:bg-[#111E2C] sticky flex items-center gap-5 bottom-[55px] md:bottom-0 px-[16px] md:px-[24px] rounded-b-[12px]"
						style={{
							boxShadow: 'rgba(0, 0, 0, 0.05) 0px -2px 4px 0px',
						}}
					>
						<Button
							type="button"
							color="purplePlaced"
							className="my-3 w-full"
							onClick={() => {
								const finalUserPreference = {
									experienceLevel: userPreference?.experienceLevel,
									careerPath: userPreference?.careerPath,
									developmentRole: developmentRoles.includes(DevelopmentRoleType.OTHER)
										? DevelopmentRoleType.OTHER
										: userPreference?.developmentRole,
									techStack:
										developmentRoles.includes(DevelopmentRoleType.OTHER) || techStacks.includes(TechStackType.OTHER)
											? TechStackType.OTHER
											: userPreference?.techStack,
									assessmentDuration: userPreference?.assessmentDuration,
									assessmentDeadline: userPreference?.newAssignmentDeadline,
								};

								if (salaryRange) {
									const requestData = {
										userPreferenceSnapshot: finalUserPreference,
										salaryRange: salaryRange,
										workingMode: workingMode ?? undefined,
										...(userPreference?.newAssignmentDeadline && {
											assessmentDeadline: userPreference?.newAssignmentDeadline,
										}),
									};

									setBenchmarkingFormData(requestData);
									navigate('/benchmarking/create-hiring-assessment/parameter-weightage');
								}
							}}
							disabled={
								!userPreference?.careerPath ||
								(!userPreference?.developmentRole && !developmentRoles.includes(DevelopmentRoleType.OTHER)) ||
								(!userPreference?.techStack &&
									!techStacks.includes(TechStackType.OTHER) &&
									!developmentRoles.includes(DevelopmentRoleType.OTHER)) ||
								!salaryRange ||
								!(typeof salaryRange?.min === 'number' && !isNaN(salaryRange?.min) && salaryRange?.min >= 0) ||
								!(typeof salaryRange?.max === 'number' && !isNaN(salaryRange?.max) && salaryRange?.max > 0) ||
								salaryRange?.min > salaryRange?.max ||
								!workingMode
								// !userPreference?.assessmentDuration ||
								// createHiringAssessmentMutation.isLoading
								// 	||
								// updateUserPreferenceMutation.isLoading
							}
						>
							{'Next'}
						</Button>
					</div>
				</div>
			</div>
		</Container>
	);
};

export default CreateHiringAssessment;
